import { Injectable, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { TranslocoService } from '@jsverse/transloco'
import { LUCIDE_ICONS } from '@/shared/components/base/base-icon/base-icon.component'
import { AUTH_PASSWORD_MIN_CHARS, AUTH_USERNAME_MIN_CHARS } from '@/constants/auth'
import { FormValidators } from '@/helpers/form-validators'
import { AuthService, LoginCredentials } from '@/services/auth.service'
import { injectLogger } from '@/services/logger.service'
import { ModalService } from '@/services/modal.service'

/**
 * Everything a login form does, minus the markup. Each layout component
 * provides its own instance, so the layouts stay presentational.
 */
@Injectable()
export class LoginFormController {
	public readonly form: FormGroup = new FormGroup({
		'username': new FormControl(null, [
			Validators.required,
			Validators.minLength(AUTH_USERNAME_MIN_CHARS),
			FormValidators.forbiddenUsernamesValidator.bind(this)
		]),
		'password': new FormControl(null, [
			Validators.required,
			Validators.minLength(AUTH_PASSWORD_MIN_CHARS)
		])
	})

	/** The login request is in flight. A second submit meanwhile is ignored. */
	public readonly submitting = signal(false)
	/** The form-level error under the fields, for a layout that shows errors inline. */
	public readonly error = signal<string | null>(null)
	// Set by the first submit, so an untouched field shows its error only once the player tried.
	private readonly attempted = signal(false)

	public readonly passwordType = signal<'password' | 'text'>('password')
	public readonly passwordIcon = computed(
		() => this.passwordType() === 'password' ? LUCIDE_ICONS['EyeOff'] : LUCIDE_ICONS['Eye']
	)

	private readonly logger = injectLogger('LoginFormController')
	private readonly authService = inject(AuthService)
	private readonly modalService = inject(ModalService)
	private readonly translocoService = inject(TranslocoService)

	constructor() {
		// The last attempt's error no longer applies once the player edits a field.
		this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.error.set(null))
	}

	/**
	 * True once logged in, false when the attempt failed, null when there is nothing to report.
	 * inlineErrors shows the failure in the form (fieldError, error); otherwise it opens in a modal.
	 */
	public async submit(redirectOnSuccess: boolean, inlineErrors = false): Promise<boolean | null> {
		if (this.submitting()) return null

		this.attempted.set(true)
		if (!this.form.valid) {
			if (inlineErrors) {
				// A missing field says so under itself; a username the site refuses reads as a failed login.
				if (this.form.controls['username'].hasError('nameIsForbidden')) {
					this.error.set(this.translocoService.translate('message.error.loginFailedDescription'))
				}
				return null
			}
			// TODO: Notify with toast instead of modal
			this.modalService.open('default', {
				title: this.translocoService.translate('message.error.loginFailed'),
				body: this.translocoService.translate('message.error.loginFailedDescription')
			})
			return null
		}

		const credentials: LoginCredentials = {
			username: this.form.value.username,
			password: this.form.value.password
		}

		this.submitting.set(true)
		this.error.set(null)
		try {
			const session = await this.authService.login(credentials, redirectOnSuccess, !inlineErrors)
			this.form.reset()
			this.attempted.set(false)
			return session && session.authenticated ? true : null
		} catch (err) {
			this.logger.error('Login error:', err)
			if (inlineErrors) {
				this.error.set(this.authService.loginErrorMessage(err))
			}
			return false
		} finally {
			this.submitting.set(false)
		}
	}

	/** The translation key of a field's error, once the player tried to log in. */
	public fieldError(name: 'username' | 'password'): string | null {
		if (!this.attempted() || !this.form.controls[name].hasError('required')) return null
		return name === 'username' ? 'message.error.usernameRequired' : 'message.error.passwordRequired'
	}

	public togglePasswordVisibility(): void {
		this.passwordType.update((type) => type === 'password' ? 'text' : 'password')
	}

	/** Opening it closes whatever modal is open, the login modal included. */
	public forgotPassword(): void {
		void this.modalService.open('resetPassword')
	}
}
